import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';

import useForkRef from '../hooks/useForkRef';
import useCharacterLimit from './hooks/useCharacterLimit';
import { InputProps } from './types';
import { getInputClasses, getInputTypographyVariant } from './utils';

/**
 * autofocus prop has been changed from mount only to render as prop changes
 */
const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    appearance = 'outline',
    characterLimit,
    color = 'text-text-primary',
    endAdornment: endAdornmentProp,
    inputProps: inputPropsProp,
    inputRef,
    onChange: onChangeProp,
    onChangeText,
    shape = 'square',
    size = 'medium',
    startAdornment,
    value,
    variant,
    ...rest
  } = props;

  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const forkedInputRef = useForkRef(internalInputRef, inputRef);

  useEffect(() => {
    if (props.autoFocus && internalInputRef.current) {
      internalInputRef.current.focus();
      //developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange

      const validInputTypes = ['text', 'search', 'url', 'tel', 'password'];
      // focusing on the end of the input
      // validation of input type is one of the above or it is a textarea
      if (
        validInputTypes.includes(internalInputRef.current.type) ||
        internalInputRef.current.tagName.toLowerCase() === 'textarea'
      ) {
        internalInputRef.current.setSelectionRange(
          internalInputRef.current.value.length,
          internalInputRef.current.value.length,
        );
      }
    }
  }, [internalInputRef, props.autoFocus]);

  const typographyVariant = getInputTypographyVariant({ size, variant });
  const { endAdornment, inputProps } = useCharacterLimit({
    characterLimit,
    endAdornment: endAdornmentProp,
    inputProps: inputPropsProp,
    value,
    variant: typographyVariant,
  });

  const classes = useMemo(
    () =>
      getInputClasses({
        appearance,
        color,
        endAdornment,
        shape,
        size,
        startAdornment,
        variant: typographyVariant,
      }),
    [appearance, color, endAdornment, shape, size, startAdornment, typographyVariant],
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeProp?.(event);
      onChangeText?.(event.target.value);
    },
    [onChangeProp, onChangeText],
  );

  return (
    <OutlinedInput
      ref={ref}
      classes={classes}
      color="primary"
      endAdornment={endAdornment}
      inputProps={inputProps}
      inputRef={forkedInputRef}
      label={null}
      notched={false}
      startAdornment={startAdornment}
      value={value}
      onChange={onChange}
      {...rest}
    />
  );
});

export default React.memo(Input);
